import styles from '../index.module.css'

import { RxLinkBreak2 } from '@react-icons/all-files/rx/RxLinkBreak2'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'

import { UNIFIED_SEARCH_ENABLE } from '@disco/constants/FeatureTypes'
import {
  clearLinkInfo,
  fetchLinkInfo,
  fetchResults,
  selectIsMdsSearchEnabled,
  selectSearchPage,
  selectSearchResetCount,
  setFilters,
  setQuery,
  setSearchType,
} from '@disco/state-management/reducers/search-page'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import {
  getMusicProviderFromSearchQuery,
  getMusicProviderFromUrl,
} from '@disco/utils/links'

import type { SearchType } from 'types/search-page'

import {
  Box,
  Button,
  DiscoCallout,
  Flex,
  SearchTextField,
  SearchTypeSelector,
  Separator,
} from 'components/ds'
import type { SearchTypeOption } from 'components/ds/SearchTypeSelector'

import { useAnimatedTextPlaceholder } from 'hooks/use-animated-text-placeholder'
import { useDispatch, useSelector } from 'hooks/use-redux'

import { getPlaceholderForSearchType } from '../utils'
import { usePromptSearchEnabled } from '../utils/use-prompt-search-enabled'
import { useSimSearchEnabled } from '../utils/use-sim-search-enabled'
import AiSearchOverlay from './ai-search-overlay'
import AiSearchToggle from './ai-search-toggle'
import LinkPreview from './link-preview'
import { MdsScopeToggle } from './mds-scope-toggle'
import { useAiSearchToggle } from './use-ai-search-toggle'

const TOP_BAR_ANIMATION_DURATION = 1800

type Props = {
  searchTypeOptions: SearchTypeOption<SearchType>[]
  hasError?: boolean
}

const SearchBar = ({ searchTypeOptions, hasError }: Props): React.ReactNode => {
  const dispatch = useDispatch()
  const { query, searchType, isLoading, linkInfo } =
    useSelector(selectSearchPage)

  const unifiedSearchEnabled = Boolean(
    useSelector(state => getConstanceSetting(state, UNIFIED_SEARCH_ENABLE)),
  )

  const promptSearchAvailable = usePromptSearchEnabled()
  const mdsSearchEnabled = useSelector(selectIsMdsSearchEnabled)

  const simSearchEnabled = useSimSearchEnabled()
  const [urlAccessError, setUrlAccessError] = useState(false)

  // Top bar animation
  const [showTopBar, setShowTopBar] = useState(false)
  const [topBarKey, setTopBarKey] = useState(0)
  const topBarTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const triggerTopBar = useCallback(() => {
    if (topBarTimerRef.current) clearTimeout(topBarTimerRef.current)
    setTopBarKey(k => k + 1)
    setShowTopBar(true)
    topBarTimerRef.current = setTimeout(
      () => setShowTopBar(false),
      TOP_BAR_ANIMATION_DURATION,
    )
  }, [])

  useEffect(() => {
    return () => {
      if (topBarTimerRef.current) clearTimeout(topBarTimerRef.current)
    }
  }, [])

  const {
    aiMode,
    aiPrompt,
    setAiMode,
    setAiPrompt,
    clearPrompt,
    closeOverlay,
    rootRef,
  } = useAiSearchToggle()

  const resetCount = useSelector(selectSearchResetCount)
  const isFirstResetRef = useRef(true)

  // clearSearch (the Reset button) bumps resetCount so we can turn the AI
  // toggle off explicitly, without tying it to the query simply being empty
  // (that would also fire when the user clears the input themselves).
  useEffect(() => {
    if (isFirstResetRef.current) {
      isFirstResetRef.current = false
      return
    }
    setAiMode('off')
    clearPrompt()
  }, [resetCount, setAiMode, clearPrompt])

  useEffect(() => {
    if (
      promptSearchAvailable &&
      searchType === 'similarity' &&
      aiMode === 'off'
    ) {
      setAiMode('active')
    }
  }, [promptSearchAvailable, searchType, aiMode, setAiMode])

  useEffect(() => {
    setUrlAccessError(false)
  }, [query])

  const openOverlay = useCallback(() => {
    if (query.trim()) {
      setAiPrompt(query)
    }
    setAiMode('overlay')
  }, [query, setAiPrompt, setAiMode])

  // When AI search is active, refocusing the input should re-expand the
  // overlay so the user can edit their prompt in full AI mode rather than
  // the collapsed keyword layout. Keyword mode ('off') focus is unaffected.
  const handleFocus = useCallback(() => {
    if (aiMode === 'active') {
      openOverlay()
    }
  }, [aiMode, openOverlay])

  // Explicitly turning the AI toggle off is treated as intent to run a search
  // for the current query (e.g. after an AI search returned results). A URL
  // always runs similarity search; anything else runs a plain keyword search.
  // Mirrors the URL/keyword branches of handleSearch.
  const handleAiToggleDeactivate = useCallback(() => {
    setUrlAccessError(false)

    if (query.trim() && getMusicProviderFromSearchQuery(query)) {
      // URLs always trigger similarity search regardless of the toggle.
      if (!simSearchEnabled) {
        setUrlAccessError(true)
        return
      }
      dispatch(setSearchType('similarity'))
      setAiMode('active')
      dispatch(fetchLinkInfo(query))
      if (!isLoading) dispatch(fetchResults())
      return
    }

    setAiMode('off')
    clearPrompt()
    if (query.trim() && !isLoading) {
      dispatch(setSearchType('keyword'))
      dispatch(fetchResults())
    }
  }, [query, simSearchEnabled, isLoading, setAiMode, clearPrompt, dispatch])

  const searchFieldPlaceholder = getPlaceholderForSearchType(searchType)
  const animatedSearchFieldPlaceholder = useAnimatedTextPlaceholder({
    phrase: searchFieldPlaceholder,
    typingSpeed: 30,
    loop: true,
    maxLoops: 2,
    pauseTime: 5000,
    enabled: !unifiedSearchEnabled,
  })

  const pageIsInEnglish = document.documentElement.lang === 'en'

  const handleSearch = () => {
    if (isLoading) return

    // Clear any previous URL access error
    setUrlAccessError(false)

    // Determine search type based on toggle state and URL detection
    if (unifiedSearchEnabled) {
      const isUrl = Boolean(query && getMusicProviderFromSearchQuery(query))

      if (isUrl) {
        // Check if user has simsearch access
        if (!simSearchEnabled) {
          setUrlAccessError(true)
          return
        }
        // URL always triggers similarity search regardless of toggle state.
        // Only activate AI toggle when it's visible (promptSearchAvailable).
        // Sim-Search-only users don't have the toggle, so we shouldn't
        // set aiMode or they'd get routed to prompt search on next query.
        dispatch(setSearchType('similarity'))
        if (promptSearchAvailable) {
          setAiMode('active')
        }
        dispatch(fetchLinkInfo(query))
      } else if (aiMode === 'active') {
        // Toggle ON: LLM/prompt search
        dispatch(setSearchType('prompt'))
      } else {
        // Toggle OFF + no URL: keyword search
        dispatch(setSearchType('keyword'))
      }
    } else {
      // Non-unified search: fetch link info for similarity search with supported URLs
      if (
        searchType === 'similarity' &&
        query &&
        getMusicProviderFromUrl(query)
      ) {
        dispatch(fetchLinkInfo(query))
      }
    }

    dispatch(fetchResults())
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (unifiedSearchEnabled && e.key === 'Tab') {
      if (aiMode === 'overlay') {
        // Close overlay and let Tab continue to next element (no preventDefault)
        // Uses closeOverlay to preserve prompt text without touching query
        closeOverlay()
      } else if (aiMode === 'off') {
        // Only open overlay via Tab when AI mode is off
        // When 'active', let Tab navigate normally to next element
        e.preventDefault()
        openOverlay()
      }
      return
    }
    if (e.key === 'Enter' && !isLoading) {
      handleSearch()
    }
  }

  const handleDismissLinkPreview = () => {
    // Unified mode: dismissing the preview returns to keyword mode (which
    // also clears linkInfo/context). Classic mode: the user picked
    // "Similarity" in the selector, so keep the search type.
    if (unifiedSearchEnabled) {
      dispatch(setSearchType('keyword'))
    }
    dispatch(clearLinkInfo())
    dispatch(setQuery(''))
  }

  const handleSearchTypeChange = useCallback(
    (value: SearchType) => {
      dispatch(setSearchType(value))
      if (value !== 'keyword') {
        dispatch(setFilters([]))
      }
    },
    [dispatch],
  )

  const handleAiSearch = useCallback(() => {
    if (!aiPrompt.trim() || isLoading) return

    setUrlAccessError(false)
    const isUrl = Boolean(getMusicProviderFromSearchQuery(aiPrompt))

    // Defensive check: guard against URL simsearch if access not available
    if (isUrl && !simSearchEnabled) {
      setUrlAccessError(true)
      setAiMode('off')
      return
    }

    dispatch(setQuery(aiPrompt))

    // URL always triggers similarity search, otherwise LLM search
    if (isUrl) {
      dispatch(setSearchType('similarity'))
      dispatch(fetchLinkInfo(aiPrompt))
    } else {
      dispatch(setSearchType('prompt'))
    }

    dispatch(fetchResults())
    triggerTopBar()
    if (promptSearchAvailable) {
      setAiMode('active')
    }
    clearPrompt()
  }, [
    aiPrompt,
    isLoading,
    simSearchEnabled,
    promptSearchAvailable,
    dispatch,
    triggerTopBar,
    setAiMode,
    clearPrompt,
  ])

  // Show link preview instead of text field when linkInfo is available
  const showLinkPreview = linkInfo && searchType === 'similarity'

  if (unifiedSearchEnabled) {
    return (
      <Box ref={rootRef} className={styles.unifiedInputWrapper}>
        {showTopBar && <div key={topBarKey} className={styles.topRainbowBar} />}
        <SearchTextField
          data-testid="unified-search-input"
          value={showLinkPreview ? '' : query}
          onChange={e => dispatch(setQuery(e.target.value))}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          placeholder={
            showLinkPreview
              ? ''
              : pageIsInEnglish
              ? promptSearchAvailable
                ? 'Search Keywords or Press Tab for AI Search'
                : simSearchEnabled
                ? 'Search Keywords or Paste a link'
                : 'Search Keywords'
              : searchFieldPlaceholder
          }
          className={`${styles.SearchTextField} ${styles.unifiedSearchInput} ${
            hasError ? styles.error : ''
          }`}
          iconPosition="left"
          color={hasError ? 'red' : undefined}
          nodeLeft={
            showLinkPreview ? (
              <LinkPreview
                linkInfo={linkInfo}
                onDismiss={handleDismissLinkPreview}
              />
            ) : undefined
          }
          nodeRight={
            promptSearchAvailable || mdsSearchEnabled ? (
              <Flex align="center" gap="2">
                {promptSearchAvailable && (
                  <AiSearchToggle
                    active={aiMode !== 'off'}
                    onActivate={openOverlay}
                    onDeactivate={handleAiToggleDeactivate}
                  />
                )}
                {promptSearchAvailable && mdsSearchEnabled && (
                  <Separator orientation="vertical" size="1" />
                )}
                {mdsSearchEnabled && <MdsScopeToggle />}
              </Flex>
            ) : undefined
          }
        />
        {promptSearchAvailable && aiMode === 'overlay' && (
          <AiSearchOverlay
            value={aiPrompt}
            onChange={setAiPrompt}
            onClear={clearPrompt}
            onSubmit={handleAiSearch}
          />
        )}
        {urlAccessError && pageIsInEnglish && (
          <DiscoCallout
            type="error"
            variant="soft"
            size="1"
            icon={<RxLinkBreak2 />}
          >
            Ready to find the perfect match for this link?{' '}
            <Link className="text-link" to="/settings/billing">
              Upgrade now
            </Link>{' '}
            to get Similarity Search.
          </DiscoCallout>
        )}
      </Box>
    )
  }

  return (
    <Flex direction="row" gap="2" minWidth="0" align="center">
      <Flex direction="row" gap="0" minWidth="0">
        <SearchTypeSelector
          value={searchType || 'keyword'}
          options={searchTypeOptions}
          onChange={handleSearchTypeChange}
        />
        {showLinkPreview ? (
          <Flex
            className={`${styles.SearchTextField} ${styles.noLeftRadius} ${styles.linkPreviewWrapper}`}
          >
            <LinkPreview
              linkInfo={linkInfo}
              onDismiss={handleDismissLinkPreview}
            />
          </Flex>
        ) : (
          <SearchTextField
            value={query}
            onChange={e => dispatch(setQuery(e.target.value))}
            onKeyDown={handleKeyDown}
            placeholder={
              pageIsInEnglish
                ? animatedSearchFieldPlaceholder
                : searchFieldPlaceholder
            }
            className={`${styles.SearchTextField} ${styles.noLeftRadius} ${
              hasError ? styles.error : ''
            }`}
            iconPosition="right"
            color={hasError ? 'red' : undefined}
          />
        )}
      </Flex>
      <Button
        onClick={handleSearch}
        disabled={isLoading}
        size="2"
        variant="solid"
      >
        Search
      </Button>
    </Flex>
  )
}

export default SearchBar
